import { Howl } from 'howler';

type OnLoadCallback = () => void;
type OnEndCallback = () => void;
type OnTrackEndCallback = () => void;
type OnErrorCallback = (id: number, error: string) => void;
type OnStopCallback = () => void;
type OnProgressCallback = (position: number) => void;
type OnPauseCallback = () => void;
type OnResumeCallback = () => void;

type AudioState = 'unloaded' | 'loading' | 'loaded' | 'playing' | 'paused' | 'ended' | 'stopped';

class AudioManager {
  private static instance: AudioManager;
  private currentHowl: Howl | null;
  private onLoadListeners: OnLoadCallback[];
  private onEndListeners: OnEndCallback[];
  private onTrackEndListeners: OnTrackEndCallback[];
  private onLoadErrorListeners: OnErrorCallback[];
  private onPlayErrorListeners: OnErrorCallback[];
  private onStopListeners: OnStopCallback[];
  private onProgressListeners: OnProgressCallback[];
  private onPauseListeners: OnPauseCallback[];
  private onResumeListeners: OnResumeCallback[];
  private state: AudioState;
  private retryCount: number;
  private maxRetries: number;
  private audioQueue: string[];
  private currentAudioIndex: number;
  private isStopping: boolean;
  public isMuted: boolean;
  private curTime: number; // 当前音频播放时间
  private duration: number; // 当前音频时长
  private progressInterval: NodeJS.Timeout | null;

  private constructor() {
    this.currentHowl = null;
    this.state = 'unloaded';
    this.retryCount = 0;
    this.maxRetries = 3;
    this.onLoadListeners = [];
    this.onEndListeners = [];
    this.onTrackEndListeners = [];
    this.onLoadErrorListeners = [];
    this.onPlayErrorListeners = [];
    this.onStopListeners = [];
    this.onProgressListeners = [];
    this.onPauseListeners = [];
    this.onResumeListeners = [];
    this.audioQueue = [];
    this.currentAudioIndex = 0;
    this.isStopping = false;
    this.isMuted = false;
    this.curTime = 0;
    this.duration = 0;
    this.progressInterval = null;
  }

  public static getInstance(): AudioManager {
    if (!AudioManager.instance) {
      AudioManager.instance = new AudioManager();
    }
    return AudioManager.instance;
  }

  // 仅需要要用户未操作播放音频的组件提前调用即可
  public initializeAudioContext(): void {
    if (!this.currentHowl) {
      this.currentHowl = new Howl({
        src: [''],
        pool: 16,
      });
    }
  }

  public play(srcArray: string[], onEndCallback?: () => void): void {
    if (!Array.isArray(srcArray)) {
      throw new Error('srcArray must be an array of audio file paths.');
    }
  
    // Compare the new srcArray with the current audioQueue
    const isSameSource = srcArray.length === this.audioQueue.length &&
      srcArray.every((src, index) => src === this.audioQueue[index]);
  
    // If the sources are the same and the audio is already playing, return early
    if (isSameSource && this.state === 'playing') {
      console.log('The audio source is the same, not restarting playback.');
      return;
    }
  
    this.audioQueue = srcArray;
    this.currentAudioIndex = 0;
  
    this.playCurrentAudio(onEndCallback);
  }  

  private playCurrentAudio(onEndCallback?: () => void): void {
    if (this.currentAudioIndex >= this.audioQueue.length) {
      this.cleanup();
      this.triggerEvent(this.onEndListeners);
      if (typeof onEndCallback === 'function') {
        onEndCallback();
      }
      return;
    }

    const currentSrc = this.audioQueue[this.currentAudioIndex];
    if (!currentSrc) {
      return;
    }
    this.stop(false); // Do not trigger stop event
    this.state = 'loading';
    this.currentHowl?.stop();
    this.currentHowl?.unload();
    this.currentHowl?.off();
    
    const sound = new Howl({
      src: [currentSrc],
      html5: true,
      pool: 16,
      preload: 'metadata',
      mute: this.isMuted ? true : false,
      onload: () => {
        this.state = 'loaded';
        this.duration = sound ? sound.duration() : 0;
        this.triggerEvent(this.onLoadListeners);
        sound?.stop();
        sound.play();
        this.state = 'playing';
        this.startProgressUpdates(sound);
      },
      onend: () => {
        this.triggerEvent(this.onTrackEndListeners); // Trigger track end event
        this.state = 'ended';
        this.currentAudioIndex++;
        this.playCurrentAudio(onEndCallback);
      },
      onloaderror: (id: number, error: any) => {
        this.triggerErrorEvent(this.onLoadErrorListeners, id, error);
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          console.log(`Retrying load (${this.retryCount}/${this.maxRetries})...`);
          this.playCurrentAudio(onEndCallback);
        } else {
          this.state = 'unloaded';
          this.cleanup();
          this.retryCount = 0;
        }
      },
      onplayerror: (id: number, error: any) => {
        this.triggerErrorEvent(this.onPlayErrorListeners, id, error);
        if (this.retryCount < this.maxRetries) {
          this.retryCount++;
          console.log(`Retrying play (${this.retryCount}/${this.maxRetries})...`);
          this.playCurrentAudio(onEndCallback);
        } else {
          this.state = 'unloaded';
          this.cleanup();
          this.retryCount = 0;
        }
      }
    });
    this.currentHowl = sound;
  }

  public pause(): void {
    if (this.currentHowl && this.state === 'playing') {
      this.currentHowl.pause();
      this.state = 'paused';
      this.stopProgressUpdates();
      this.triggerEvent(this.onPauseListeners);
    }
  }

  public resume(): void {
    if (this.currentHowl && this.state === 'paused') {
      this.currentHowl?.stop();
      this.currentHowl.play();
      this.state = 'playing';
      this.startProgressUpdates(this.currentHowl);
      this.triggerEvent(this.onResumeListeners);
    }
  }

  public seekForward(seconds: number): void {
    if (this.currentHowl && (this.state === 'playing' || this.state === 'paused')) {
      const currentPosition = this.currentHowl.seek() as number;
      this.currentHowl.seek(currentPosition + seconds);
    }
  }

  public seekBackward(seconds: number): void {
    if (this.currentHowl && (this.state === 'playing' || this.state === 'paused')) {
      const currentPosition = this.currentHowl.seek() as number;
      this.currentHowl.seek(Math.max(0, currentPosition - seconds));
    }
  }

  public seekTo(seconds: number): void {
    if (this.currentHowl && (this.state === 'playing' || this.state === 'paused')) {
      this.currentHowl.seek(seconds);
    }
  }

  public stop(triggerEvent: boolean = true): void {
    if (this.currentHowl) {
      this.isStopping = true;
      this.currentHowl.stop();
      this.currentHowl.unload();
      this.currentHowl.off();
      this.currentHowl = null;
      this.state = 'stopped';
      this.stopProgressUpdates();
      if (triggerEvent) {
        this.triggerEvent(this.onStopListeners);
      }
      this.isStopping = false;
    }
  }

  public mute(): void {
    if (this.currentHowl) {
      this.currentHowl.mute(true);
    }
    this.isMuted = true;
  }

  public unmute(): void {
    if (this.currentHowl) {
      this.currentHowl.mute(false);
    }
    this.isMuted = false;
  }

  public addEventListener(event: 'onload', callback: OnLoadCallback): void;
  public addEventListener(event: 'onend', callback: OnEndCallback): void;
  public addEventListener(event: 'ontrackend', callback: OnTrackEndCallback): void;
  public addEventListener(event: 'onloaderror', callback: OnErrorCallback): void;
  public addEventListener(event: 'onplayerror', callback: OnErrorCallback): void;
  public addEventListener(event: 'onstop', callback: OnStopCallback): void;
  public addEventListener(event: 'onprogress', callback: OnProgressCallback): void;
  public addEventListener(event: 'onpause', callback: OnPauseCallback): void;
  public addEventListener(event: 'onresume', callback: OnResumeCallback): void;
  public addEventListener(event: string, callback: any): void {
    if (event === 'onload') {
      this.onLoadListeners.push(callback);
    } else if (event === 'onend') {
      this.onEndListeners.push(callback);
    } else if (event === 'ontrackend') {
      this.onTrackEndListeners.push(callback);
      if (this.onTrackEndListeners.length > 1) {
        console.error('多个监听ontrackend--->', this.onTrackEndListeners);
      }
    } else if (event === 'onloaderror') {
      this.onLoadErrorListeners.push(callback);
    } else if (event === 'onplayerror') {
      this.onPlayErrorListeners.push(callback);
    } else if (event === 'onstop') {
      this.onStopListeners.push(callback);
    } else if (event === 'onprogress') {
      this.onProgressListeners.push(callback);
    } else if (event === 'onpause') {
      this.onPauseListeners.push(callback);
    } else if (event === 'onresume') {
      this.onResumeListeners.push(callback);
    }
  }

  public removeEventListener(event: 'onload', callback: OnLoadCallback): void;
  public removeEventListener(event: 'onend', callback: OnEndCallback): void;
  public removeEventListener(event: 'ontrackend', callback: OnTrackEndCallback): void;
  public removeEventListener(event: 'onloaderror', callback: OnErrorCallback): void;
  public removeEventListener(event: 'onplayerror', callback: OnErrorCallback): void;
  public removeEventListener(event: 'onstop', callback: OnStopCallback): void;
  public removeEventListener(event: 'onprogress', callback: OnProgressCallback): void;
  public removeEventListener(event: 'onpause', callback: OnPauseCallback): void;
  public removeEventListener(event: 'onresume', callback: OnResumeCallback): void;
  public removeEventListener(event: string, callback: any): void {
    if (event === 'onload') {
      this.onLoadListeners = this.onLoadListeners.filter(listener => listener !== callback);
    } else if (event === 'onend') {
      this.onEndListeners = this.onEndListeners.filter(listener => listener !== callback);
    } else if (event === 'ontrackend') {
      this.onTrackEndListeners = this.onTrackEndListeners.filter(listener => listener !== callback);
    } else if (event === 'onloaderror') {
      this.onLoadErrorListeners = this.onLoadErrorListeners.filter(listener => listener !== callback);
    } else if (event === 'onplayerror') {
      this.onPlayErrorListeners = this.onPlayErrorListeners.filter(listener => listener !== callback);
    } else if (event === 'onstop') {
      this.onStopListeners = this.onStopListeners.filter(listener => listener !== callback);
    } else if (event === 'onprogress') {
      this.onProgressListeners = this.onProgressListeners.filter(listener => listener !== callback);
    } else if (event === 'onpause') {
      this.onPauseListeners = this.onPauseListeners.filter(listener => listener !== callback);
    } else if (event === 'onresume') {
      this.onResumeListeners = this.onResumeListeners.filter(listener => listener !== callback);
    }
  }

  public destroy(): void {
    this.cleanup();
    
    // 清理所有定时器
    this.stopProgressUpdates();
    
    // 重置所有状态
    this.state = 'unloaded';
    this.audioQueue = [];
    this.currentAudioIndex = 0;
    this.retryCount = 0;
    this.isStopping = false;
    this.curTime = 0;
    this.duration = 0;
    
    // 清理所有事件监听器
    this.onLoadListeners = [];
    this.onEndListeners = [];
    this.onTrackEndListeners = [];
    this.onLoadErrorListeners = [];
    this.onPlayErrorListeners = [];
    this.onStopListeners = [];
    this.onProgressListeners = [];
    this.onPauseListeners = [];
    this.onResumeListeners = [];
  }

  public getAudioList(): string[] {
    return this.audioQueue;
  }

  public getState(): AudioState {
    return this.state;
  }

  public getCurTime(): number {
    return this.curTime;
  }

  public getDuration(): number {
    return this.duration;
  }

  public getCurrentPosition(sound: any): number | null {
    if (sound && (this.state === 'playing' || this.state === 'paused')) {
      return sound.seek() as number;
    }
    return null;
  }

  private startProgressUpdates(sound: any): void {
    this.stopProgressUpdates();

    this.progressInterval = setInterval(() => {
      const position = this.getCurrentPosition(sound);
      if (position !== null) {
        this.triggerProgressEvent(position);
        this.curTime = position;
      }
    }, 200);
  }

  private stopProgressUpdates(): void {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  private triggerProgressEvent(position: number): void {
    this.onProgressListeners.forEach(callback => {
      if (typeof callback === 'function') {
        callback(position);
      }
    });
  }

  private triggerEvent(listeners: OnLoadCallback[] | OnEndCallback[] | OnTrackEndCallback[] | OnStopCallback[]): void {
    listeners.forEach(callback => {
      if (typeof callback === 'function') {
        callback();
      }
    });
  }

  private triggerErrorEvent(listeners: OnErrorCallback[], id: number, error: string): void {
    listeners.forEach(callback => {
      if (typeof callback === 'function') {
        callback(id, error);
      }
    });
  }

  private cleanup(): void {
    if (this.currentHowl) {
      this.currentHowl.stop();
      this.currentHowl.unload();
      this.currentHowl.off();
      this.currentHowl = null;
      if (!this.isStopping) {
        this.state = 'unloaded';
      }
    }
  }
}

// Singleton instance
const audioManager = AudioManager.getInstance();

// Export the instance for use in other modules
export default audioManager;
